import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@smartling-ui/button";

import { CompanyNameField } from "../CompanyNameField";
import { JobTitleField } from "../JobTitleField";
import { AcceptTermsField } from "../AcceptTermsField";
import { GoogleFormType } from "../../types";
import { redirectToGoogle } from "../../utils";
import { getGoogleFormSchema } from "../RegistrationFormController/utils";

import messages from "./messages";
import * as styles from "./styles.module.css";

type Props = {
    productUid: string;
    hasDeveloperSandbox: boolean;
    initialCompanyName: string;
    initialJobTitle: string;
    hsCtaAttrib: string;
}

export function GoogleRegistrationForm({
    productUid,
    hasDeveloperSandbox,
    initialCompanyName,
    initialJobTitle,
    hsCtaAttrib
}: Props) {
    const intl = useIntl();

    const schema = useMemo(
        () => getGoogleFormSchema(intl, hasDeveloperSandbox),
        [intl, hasDeveloperSandbox]
    );

    const { control, handleSubmit } = useForm<GoogleFormType>({
        resolver: zodResolver(schema),
        mode: "onTouched",
        defaultValues: {
            companyName: initialCompanyName,
            jobTitle: initialJobTitle,
            acceptPrivacyPolicy: false
        }
    });

    const onSubmit = useCallback((form: GoogleFormType) => {
        redirectToGoogle(
            productUid,
            hasDeveloperSandbox ? form.companyName.trim() : undefined,
            hasDeveloperSandbox ? form.jobTitle.trim() : undefined,
            hsCtaAttrib
        );
    }, [productUid, hasDeveloperSandbox, hsCtaAttrib]);

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className={styles.form}
        >
            {hasDeveloperSandbox && (
                <Controller
                    name="companyName"
                    control={control}
                    // eslint-disable-next-line react/jsx-no-bind
                    render={({ field, fieldState }) => (
                        <CompanyNameField
                            autoFocus
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            inputRef={field.ref}
                            name={field.name}
                            error={fieldState.error}
                        />
                    )}
                />
            )}

            {hasDeveloperSandbox && (
                <Controller
                    name="jobTitle"
                    control={control}
                    // eslint-disable-next-line react/jsx-no-bind
                    render={({ field, fieldState }) => (
                        <JobTitleField
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            inputRef={field.ref}
                            name={field.name}
                            error={fieldState.error}
                        />
                    )}
                />
            )}

            <Controller
                name="acceptPrivacyPolicy"
                control={control}
                // eslint-disable-next-line react/jsx-no-bind
                render={({ field: rhfField, fieldState }) => (
                    <AcceptTermsField
                        checked={rhfField.value === true}
                        onChange={rhfField.onChange}
                        onBlur={rhfField.onBlur}
                        error={fieldState.error?.message}
                    />
                )}
            />

            <Button
                variant="primary"
                type="submit"
                size="lg"
                fullWidth
            >
                {intl.formatMessage(messages.submitButton)}
            </Button>
        </form>
    );
}
